import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { InjectionToken, PLATFORM_ID, assertInInjectionContext, inject } from '@angular/core';

import { MonoTypeOperatorFunction, tap } from 'rxjs';

/**
 * @description
 * RxJS operator that applies a `tap` side-effect **only when running on the server** (Angular SSR).
 *
 * Uses Angular's `PLATFORM_ID` (either provided explicitly via `platformId` or
 * injected at runtime) together with `isPlatformServer` to decide whether to run
 * the side-effect. If not on the server, this operator is a no-op.
 *
 * @typeParam T - Type of the observable value.
 * @param sideEffect - Function to run for each emission when executing on the server.
 * @param platformId - Optional `PLATFORM_ID` to avoid runtime DI (useful in pure functions or tests).
 * @returns A `MonoTypeOperatorFunction<T>` that taps only on the server.
 *
 * @example
 * ```ts
 * import { of } from 'rxjs';
 *
 * of('hello').pipe(
 *   tapIfServer(v => console.log('SSR only:', v))
 * ).subscribe(); // logs on server, no-ops in browser
 * ```
 */
export function tapIfServer<T>(sideEffect: (value: T) => void, platformId?: PlatformId): MonoTypeOperatorFunction<T> {
    if (ngDevMode && !platformId) {
        assertInInjectionContext(tapIfServer);
    }

    return tapIfPlatform(isPlatformServer, sideEffect, platformId);
}

/**
 * @description
 * RxJS operator that applies a `tap` side-effect **only when running in the browser** (Angular SSR).
 *
 * Uses Angular's `PLATFORM_ID` (either provided explicitly via `platformId` or
 * injected at runtime) together with `isPlatformBrowser` to decide whether to run
 * the side-effect. If not in the browser, this operator is a no-op.
 *
 * @typeParam T - Type of the observable value.
 * @param sideEffect - Function to run for each emission when executing in the browser.
 * @param platformId - Optional `PLATFORM_ID` to avoid runtime DI (useful in pure functions or tests).
 * @returns A `MonoTypeOperatorFunction<T>` that taps only in the browser.
 *
 * @example
 * ```ts
 * import { of } from 'rxjs';
 *
 * of('hello').pipe(
 *   tapIfBrowser(v => console.log('Browser only:', v))
 * ).subscribe(); // logs in browser, no-ops on server
 * ```
 */
export function tapIfBrowser<T>(sideEffect: (value: T) => void, platformId?: PlatformId): MonoTypeOperatorFunction<T> {
    if (ngDevMode && !platformId) {
        assertInInjectionContext(tapIfBrowser);
    }

    return tapIfPlatform(isPlatformBrowser, sideEffect, platformId);
}

/**
 * @internal
 * @description
 * Core platform-gated variant of `tap`. Executes the provided `sideEffect`
 * only if the `predicate(platformId)` returns `true`. If it returns `false`,
 * the operator is a no-op (passes through values unchanged).
 *
 * @typeParam T - Type of the observable value.
 * @param predicate - Predicate receiving the (injected or provided) `platformId`.
 * @param sideEffect - Side-effect to run for each emission when the predicate is `true`.
 * @param platformId - Optional `PLATFORM_ID` value to avoid runtime injection.
 * @returns A `MonoTypeOperatorFunction<T>` that conditionally taps.
 */
function tapIfPlatform<T>(
    predicate: (platformId: PlatformId) => boolean,
    sideEffect: (value: T) => void,
    platformId = inject(PLATFORM_ID),
): MonoTypeOperatorFunction<T> {
    if (!predicate(platformId)) {
        // No-op: just pass through
        return (source$) => source$;
    }

    // Condition true: wrap in tap
    return (source$) => source$.pipe(tap(sideEffect));
}

type TokenType<T> = T extends InjectionToken<infer U> ? U : never;
type PlatformId = TokenType<typeof PLATFORM_ID>;
